<template>
  <div
    class="contact-detail w-full min-w-0 max-w-full bg-neutral-50 flex flex-col h-full"
  >
    <contact-info
      :contact="contact"
      :show-wa-id="isOnlineWaInboxV2"
      @open-new-messsage="toggleNewConversationModal"
      @toggle-panel="onToggle"
    />

    <contact-tabs :contact="contact" />

    <!-- Bottom action buttons -->
    <div
      class="flex flex-wrap gap-2 items-center justify-center px-3 pt-4 pb-5 shrink-0"
    >
      <button
        v-if="canEditContact"
        v-tooltip="$t('EDIT_CONTACT.BUTTON_LABEL')"
        class="button-ghost p-[7px] h-[32px] size-8 only-icon"
        @click="toggleEditModal"
      >
        <span class="material-symbols-outlined text-[16px]">edit</span>
      </button>
      <button
        v-if="canEditContact"
        v-tooltip="$t('NOTES.HEADER.TITLE')"
        class="button-ghost p-[7px] h-[32px] size-8 only-icon"
        @click="$router.push(contactProfileLink)"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10.5 14.1581V12.1581H8.5V11.1581H10.5V9.15814H11.5V11.1581H13.5V12.1581H11.5V14.1581H10.5ZM2.5 12.1748V11.1748H7.1975C7.17694 11.3504 7.16944 11.5175 7.175 11.6763C7.18056 11.8351 7.19361 12.0012 7.21417 12.1748H2.5ZM2.5 9.50814V8.50814H8.818C8.62656 8.64314 8.45156 8.79397 8.293 8.96064C8.13445 9.12731 7.99022 9.3098 7.86033 9.50814H2.5ZM2.5 6.84147V5.84147H12.1667V6.84147H2.5ZM2.5 4.1748V3.1748H12.1667V4.1748H2.5Z" fill="#6B7280" style="fill:#6B7280;fill:color(display-p3 0.4196 0.4471 0.5020);fill-opacity:1;"/>
        </svg>
      </button>
      <button
        v-if="showDocalButton"
        v-tooltip="$t('DOCAL_UNSIGNED_TOOLTIP')"
        class="button-ghost p-[7px] h-[32px] size-8 only-icon"
        @click="toggleUnsignedDocuments"
      >
        <span class="material-symbols-outlined text-[16px]">draft</span>
      </button>
      <button
        v-if="canMergeContact"
        v-tooltip="$t('CONTACT_PANEL.MERGE_CONTACT')"
        class="button-ghost p-[7px] h-[32px] size-8 only-icon"
        :disabled="contactUIFlags.isMerging"
        @click="openMergeModal"
      >
        <span class="material-symbols-outlined text-[16px]">merge</span>
      </button>
      <div v-if="hasEditPermission('Contacts')">
        <button
          v-if="blockContactBtnDisabled"
          v-tooltip="
            contactPageBlockContactBtnDisabled
              ? $t('CONTACT_PANEL.DISABLE_WHATSAPP_BLOCK')
              : $t('CONTACT_PANEL.UNABLE_BLOCK')
          "
          class="button-ghost p-[7px] h-[32px] size-8 only-icon"
          disabled
        >
          <span class="material-symbols-outlined text-[16px]">no_sound</span>
        </button>
        <button
          v-else-if="!contactBlocked"
          v-tooltip="$t('CONTACT_PANEL.BLOCK_CONTACT')"
          class="button-ghost p-[7px] h-[32px] size-8 only-icon"
          @click="blockContact"
        >
          <span class="material-symbols-outlined text-[16px]">no_sound</span>
        </button>
        <button
          v-else
          v-tooltip="$t('CONTACT_PANEL.UNBLOCK_CONTACT')"
          class="button-ghost p-[7px] h-[32px] size-8 only-icon"
          @click="unBlockContact"
        >
          <span class="material-symbols-outlined text-[16px]">
            brand_awareness
          </span>
        </button>
      </div>
      <button
        v-if="hasDeletePermission('Contacts')"
        v-tooltip="$t('DELETE_CONTACT.BUTTON_LABEL')"
        class="button-ghost p-[7px] h-[32px] size-8 only-icon button-ghost--danger"
        :disabled="contactUIFlags.isDeleting"
        @click="toggleDeleteModal"
      >
        <span class="material-symbols-outlined text-[16px]">delete</span>
      </button>
    </div>

    <new-conversation
      v-if="contact.id"
      :show="showConversationModal"
      :contact="contact"
      @cancel="toggleNewConversationModal"
    />
    <docal-unsigned-modal
      :show="showUnsignedDocuments"
      :contact-id="this.contact.id"
      @close="toggleUnsignedDocuments"
    />
    <edit-contact
      v-if="showEditModal && canEditContact"
      :show="showEditModal"
      :contact="contact"
      @cancel="toggleEditModal"
    />
    <contact-merge-modal
      v-if="showMergeModal && canMergeContact"
      :primary-contact="contact"
      :show="showMergeModal"
      @close="toggleMergeModal"
    />
    <block-contact-modal ref="confirmDialog" @change="onChangeBlockDuration" />
    <woot-delete-modal
      v-if="showDeleteModal && hasDeletePermission('Contacts')"
      :show.sync="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ContactInfo from './ContactInfo.vue';
import ContactTabs from './ContactTabs.vue';
import NewConversation from 'dashboard/routes/dashboard/conversation/contact/NewConversation.vue';
import BlockContactModal from 'dashboard/routes/dashboard/conversation/contact/BlockContactModal.vue';
import EditContact from 'dashboard/routes/dashboard/conversation/contact/EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import DocalUnsignedModal from 'dashboard/routes/dashboard/conversation/contact/DocalUnsignedModal.vue';

import attributeMixin from 'dashboard/mixins/attributeMixin';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import alertMixin from 'shared/mixins/alertMixin';

import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  isAConversationRoute,
  getConversationDashboardRoute,
} from 'dashboard/helper/routeHelpers';

export default {
  name: 'ContactPanelV2',
  components: {
    ContactInfo,
    ContactTabs,
    NewConversation,
    BlockContactModal,
    EditContact,
    ContactMergeModal,
    DocalUnsignedModal,
  },
  mixins: [attributeMixin, hasPermissionMixin, alertMixin],
  props: {
    customFields: {
      type: Array,
      default: () => [],
    },
    preventBlockContact: {
      type: Boolean,
      default: false,
    },
    inConversationBox: {
      type: Boolean,
      default: false,
    },
    onToggle: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      showConversationModal: false,
      showEditModal: false,
      showMergeModal: false,
      showUnsignedDocuments: false,
      showDeleteModal: false,
      blockDuration: null,
    };
  },

  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      contactUIFlags: 'contacts/getUIFlags',
      accountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
    }),
    account() {
      return this.getAccount(this.accountId);
    },
    contact() {
      return this.$store.getters['contacts/getContact'](this.contactId);
    },
    contactId() {
      return this.currentChat.meta?.sender?.id;
    },
    isOnlineWaInboxV2() {
      const inbox = this.$store.getters['inboxes/getInbox'](
        this.currentChat.inbox_id
      );
      return inbox?.api_type === 'wa_v2';
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    newRulesSystemEnabled() {
      return this.account?.features?.new_rules_system;
    },
    canEditContact() {
      return !this.newRulesSystemEnabled || this.hasEditPermission('Contacts');
    },
    canMergeContact() {
      return (
        this.canEditContact &&
        (this.isAdmin ||
          (this.newRulesSystemEnabled && this.hasDeletePermission('Contacts')))
      );
    },
    contactPageBlockContactBtnDisabled() {
      return !this.inConversationBox && this.contact.is_whatsapp_contact;
    },
    blockContactBtnDisabled() {
      return (
        this.contactPageBlockContactBtnDisabled ||
        (this.preventBlockContact && !this.contact.blocked)
      );
    },
    contactBlocked() {
      return this.contact?.blocked;
    },
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    showDocalButton() {
      return this.account?.additional_attributes?.document_signature_enabled;
    },
  },
  watch: {
    conversationId(newConversationId, prevConversationId) {
      if (newConversationId && newConversationId !== prevConversationId) {
        this.getCurrentTicket();
      }
    },
    'currentChat.status'(newStatus) {
      if (newStatus === 'open') {
        this.getCurrentTicket();
      }

      if (newStatus === 'resolved') {
        this.$store.dispatch('tickets/conversationResolved');
      }
    },
  },
  mounted() {
    this.getCurrentTicket();
  },
  methods: {
    getCurrentTicket() {
      if (!this.conversationId) return;

      this.$store.dispatch('tickets/current', {
        conversationId: this.conversationId,
      });
    },
    isUrl(value) {
      try {
        return Boolean(new URL(value));
      } catch {
        return false;
      }
    },
    toggleNewConversationModal() {
      this.showConversationModal = !this.showConversationModal;
      bus.$emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, this.showConversationModal);
    },
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    openMergeModal() {
      this.showMergeModal = !this.showMergeModal;
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    async blockContact() {
      const ok = await this.$refs.confirmDialog.showConfirmation();
      if (ok && this.blockDuration !== null) {
        this.$store.dispatch('contacts/blockContact', {
          contactId: this.contact.id,
          conversationId: this.currentChat.id,
          period: this.blockDuration.id,
        });
        this.showAlert(this.$t('CONTACT_PANEL.BLOCKED_SUCCESS'));
      }
    },
    unBlockContact() {
      this.$store.dispatch('contacts/unblockContact', {
        contactId: this.contact.id,
        conversationId: this.currentChat.id,
      });
      this.showAlert(this.$t('CONTACT_PANEL.UNBLOCKED_SUCCESS'));
    },
    onChangeBlockDuration(duration) {
      this.blockDuration = duration;
    },
    toggleUnsignedDocuments() {
      this.showUnsignedDocuments = !this.showUnsignedDocuments;
    },
    toggleMergeModal() {
      this.showMergeModal = !this.showMergeModal;
    },
    async deleteContact() {
      try {
        const id = this.contact.id;
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panel-close');
        this.showAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        this.showAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    confirmDeletion() {
      this.deleteContact();
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showConversationModal = false;
      this.showEditModal = false;
    },
  },
};
</script>
